import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { SpiritualStats } from '../../hooks/useSpiritualStats';

interface Props {
  stats: SpiritualStats;
}

const ProgressoAccordion: React.FC<Props> = ({ stats }) => {
  const [open, setOpen] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);

  return (
    <div className="backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>

      {/* Header clicável */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors duration-150"
      >
        <div className="flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5 text-purple-400 flex-shrink-0" />
          <span className="text-base font-semibold text-white">Progresso</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-3 text-center">
            <div>
              <div className="text-base font-bold text-white leading-none">{stats.totalReadings}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">tiragens</div>
            </div>
            <div className="w-px bg-white/10" />
            <div>
              <div className="text-base font-bold text-white leading-none">{stats.consecutiveDays}</div>
              <div className="text-[10px] text-gray-400 mt-0.5">dias seguidos</div>
            </div>
          </div>
          <motion.span
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            className="text-gray-400 text-xs ml-1 flex-shrink-0"
          >
            ▼
          </motion.span>
        </div>
      </button>

      {/* Conteúdo expansível */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="progreso-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            <div className="px-6 pb-5 space-y-2 border-t border-white/10 pt-4">

              {/* Conquistas */}
              {stats.achievementsDef.map((ach) => {
                const unlocked = stats.achievementsUnlocked.includes(ach.key);
                const progress = ach.threshold
                  ? Math.min((stats.totalReadings / ach.threshold) * 100, 100)
                  : ach.streak
                  ? Math.min((stats.consecutiveDays / ach.streak) * 100, 100)
                  : unlocked ? 100 : 0;
                return (
                  <div
                    key={ach.key}
                    className="p-2.5 rounded-lg"
                    style={{
                      background: unlocked ? 'rgba(251,191,36,0.08)' : 'rgba(255,255,255,0.03)',
                      border: unlocked ? '1px solid rgba(251,191,36,0.25)' : '1px solid rgba(255,255,255,0.06)',
                      opacity: unlocked ? 1 : 0.6,
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{ach.icon}</span>
                        <div>
                          <span className={`text-xs font-semibold ${unlocked ? 'text-gold-300' : 'text-gray-300'}`}>{ach.label}</span>
                          <span className="text-[10px] text-gray-500 ml-1.5">{ach.desc}</span>
                        </div>
                      </div>
                      {unlocked
                        ? <span className="text-xs text-gold-400 flex-shrink-0">✓</span>
                        : ach.tokens > 0 && <span className="text-xs text-purple-400 flex-shrink-0">+{ach.tokens}t</span>
                      }
                    </div>
                    {!unlocked && (
                      <div className="w-full bg-gray-700/50 rounded-full h-1">
                        <div
                          className="bg-gradient-to-r from-purple-500 to-gold-500 h-1 rounded-full transition-all duration-500"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Indicações */}
              {stats.referralCode && (
                <div className="mt-1 p-3 rounded-xl border border-purple-500/20" style={{ background: 'rgba(139,92,246,0.06)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm">🤝</span>
                    <span className="text-xs font-semibold text-purple-300">Indique e ganhe tokens</span>
                    {stats.referralsCount > 0 && (
                      <span className="ml-auto text-xs text-gold-400">{stats.referralsCount} indicação{stats.referralsCount > 1 ? 'ões' : ''}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mb-2">Cada pessoa que se cadastrar pelo seu link rende +1 token.</p>
                  <button
                    onClick={() => {
                      const link = `${window.location.origin}/cadastro?ref=${stats.referralCode}`;
                      navigator.clipboard.writeText(link).then(() => {
                        setReferralCopied(true);
                        setTimeout(() => setReferralCopied(false), 2500);
                      });
                    }}
                    className="w-full py-2 rounded-lg text-xs font-semibold transition-all duration-200"
                    style={{
                      background: referralCopied ? 'rgba(34,197,94,0.15)' : 'rgba(139,92,246,0.2)',
                      border: referralCopied ? '1px solid rgba(34,197,94,0.4)' : '1px solid rgba(139,92,246,0.4)',
                      color: referralCopied ? '#86efac' : '#c4b5fd',
                    }}
                  >
                    {referralCopied ? '✓ Link copiado!' : `📋 Copiar link — ${stats.referralCode}`}
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProgressoAccordion;
